import type { IdToken, User as Auth0User } from '@auth0/auth0-react';
import type { UserRole , User } from '@/shared/types';

/**
 * Transforms the role string from Auth0 to our standardized format with underscores
 */
export const transformRoleString = (
  roleString: string | null | undefined,
): UserRole | null => {
  if (!roleString) return null;

  // Normalize the input by trimming and converting to lowercase
  const normalizedRole = roleString.trim().toLowerCase();

  // Map the role string to our standard format
  switch (normalizedRole) {
    case 'client':
      return 'client';
    case 'independent provider':
    case 'independent_provider':
      return 'independent_provider';
    case 'business owner':
    case 'shop_owner':
      return 'shop_owner';
    case 'staff':
    case 'staff provider':
    case 'staff_provider':
      return 'staff_provider';
    case 'shop_manager':
      return 'shop_manager';
    case 'pending_provider':
      return 'pending_provider' as UserRole;
    case 'pending_staff':
      return 'pending_staff' as UserRole;
    default:
      return null;
  }
};

/**
 * Extracts the user role from Auth0 claims
 */
export const extractRoleFromClaims = (
  claims: IdToken,
  namespace: string,
): UserRole | null => {
  if (!namespace) return null;

  // Normalize namespace to ensure trailing slash
  const ns = namespace.endsWith('/') ? namespace : `${namespace}/`;

  // Check for role in custom claims (set by Auth0 Post-Login Action)
  if (claims && claims[`${ns}role`]) {
    return transformRoleString(claims[`${ns}role`]);
  }

  // Fallback to checking user_metadata
  if (claims && claims[`${ns}user_metadata`]?.role) {
    return transformRoleString(claims[`${ns}user_metadata`].role);
  }

  return null;
};

/**
 * Prepares user data from Auth0 user
 */
export const prepareUserData = (auth0User: Auth0User): User => {
  return {
    ...auth0User,
    sub: auth0User.sub,
  } as User;
};

/**
 * Extract the best display name from Auth0 user object
 * Prefers given_name + family_name, falls back to name if it's not an email
 *
 * @param auth0User - Auth0 user object (or our internal User type)
 * @returns The best name to display, or empty string if no valid name found
 */
export const extractUserDisplayName = (auth0User: Auth0User | User | null | undefined): string => {
  if (!auth0User) return '';

  // Prefer given_name + family_name (most accurate for social logins)
  const givenName = 'given_name' in auth0User ? auth0User.given_name : undefined;
  const familyName = 'family_name' in auth0User ? auth0User.family_name : undefined;

  if (givenName || familyName) {
    const parts = [givenName, familyName].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(' ');
    }
  }

  // Fall back to name, but only if it doesn't look like an email
  if (auth0User.name && !auth0User.name.includes('@')) {
    return auth0User.name;
  }

  // Don't use email as name
  return '';
};
